import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import withModal from "../withModal";
import MaterialModal from "../MaterialModal";
import FormGroupWithError from "../../misc/FormGroupWithError";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { hideModal, showModal } from "../../../actions/actionCreators";
import {
    requestClientVariableGroupings,
    requestAddClientVariableGrouping,
    requestDeleteClientGrouping,
} from "../../../actions/clientVariableActions";

import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";
import { ColorButton } from "../../material-components/Misc/Button";

import { stableSort, getSorting } from "../../../helpers/materialTableFunctions";

// custom material ui table components
import TableHeader from "../../material-components/Table/TableHeader";
import TableBody from "../../material-components/Table/TableBody";
import TableFooter from "../../material-components/Table/TableFooter";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { IAppState } from "../../../types/stores/index";
import { IVariableGrouping } from "../../../types/stores/vars";
import { Autocomplete } from "@material-ui/lab";

interface IVariableGroupingModal {
    isOpen: boolean;
    modalProps: {
        scope: string;
    };
}
const theme = createTheme({
    overrides: {
        MuiInput: {
            underline: {
                "&&&&:hover:after": {
                    borderBottom: "2px solid #406eb3",
                },
                "&&&&:after": {
                    borderBottom: "2px solid #406eb3",
                },
            },
        },
    },
});

const VariableGroupingModal: React.FC<IVariableGroupingModal> = ({ isOpen }: IVariableGroupingModal) => {
    const dispatch = useDispatch();

    //Props
    const roles = useSelector((state: IAppState) => state.session.roles || []);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);
    const variableGroups = useSelector((state: IAppState) => state.vars.variableGroupings || []);
    const userId = useSelector((state: IAppState) => state.session.userId || 0);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [errors, setErrors] = useState<{ name: string; type: string }>({ name: "", type: "" });
    const [requestPending, setRequestPending] = useState(false);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("VariableName");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [filter, setFilter] = useState("");

    //Roles
    const hasSuperAdmin = roles.includes("Super Admin");
    const hasExperianAdmin = roles.includes("Experian Admin");

    //Features
    const hasClientVariablesManagement = enabledFeatures.includes("client-variables-management");

    const hide = () => dispatch(hideModal("VARIABLE_GROUPING"));
    useEffect(() => {
        if (isOpen && userId > 0) {
            dispatch(requestClientVariableGroupings());
        }
    }, [dispatch, userId, isOpen]);
    const getTitle = () => {
        let title = "Variable Grouping";

        return title;
    };

    const handleRequestSort = (_, property: string) => {
        setOrder(order === "asc" ? "desc" : "asc");
        setOrderBy(property);
    };

    const renderNewActionColumn = value => (
        <Tooltip title="Delete">
            <IconButton id="btn-delete" color="secondary" onClick={() => renderConfirmDelete(value)} size="small">
                <i className={"material-icons"} style={{ height: "16px", width: "16px", fontSize: "16px" }}>
                    delete
                </i>
            </IconButton>
        </Tooltip>
    );

    const renderConfirmDelete = (id: number) => {
        dispatch(showModal("GENERIC_DELETE", { id, executeAction: deleteItem }));
    };
    const deleteItem = (id: number) => {
        dispatch(requestDeleteClientGrouping(id));
    };

    const validate = () => {
        const errorsList = { name: "", type: "" };
        if (!name) {
            errorsList.name = "Please provide a group name.";
        }

        setErrors(errorsList);
        return Boolean(errorsList.name == "" && errorsList.type == "");
    };

    const save = () => {
        if (!validate()) {
            return;
        }

        const onAddCallback = () => {
            setRequestPending(false);
            resetValues();
        };
        const onErrorCallback = () => setRequestPending(false);
        setRequestPending(true);

        dispatch(requestAddClientVariableGrouping(name, description, onAddCallback, onErrorCallback));
    };

    const resetValues = () => {
        setName("");
        setDescription("");
        setErrors({ name: "", type: "" });
    };

    const handleChangeName = (name: string) => setName(name);
    const handleChangeDescription = (descr: string) => setDescription(descr);

    const filterVariablesByName = (): Array<IVariableGrouping> => {
        if (filter.length > 0) {
            setPage(0);
            return variableGroups.filter(x => x.GroupName.toLowerCase().includes(filter.toLowerCase()));
        }

        return variableGroups;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const tableDataFilteredByName = useMemo(() => filterVariablesByName(), [filter, variableGroups]);

    const getData = () => {
        const data: Array<Array<Array<string | number | JSX.Element | null> | boolean>> = [];
        const sortedData: Array<IVariableGrouping> = stableSort(tableDataFilteredByName, getSorting(order, orderBy));

        sortedData.map(dataRow => {
            data.push([
                ["Id", dataRow.Id, "smDown"],
                ["GroupName", dataRow.GroupName, "smDown"],
                ["Description", dataRow.Description, "smDown"],
                ["DefaultValue", dataRow.DefaultValue, "smDown"],
                ["Actions", renderNewActionColumn(dataRow.Id), "smDown"],
            ]);
        });

        return data;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = useMemo(() => getData(), [tableDataFilteredByName, order, orderBy, page, rowsPerPage, filter]);

    return hasSuperAdmin || hasExperianAdmin || hasClientVariablesManagement ? (
        <MaterialModal
            id="variable-grouping-modal"
            isOpen={isOpen}
            onClose={hide}
            title={getTitle()}
            onPrimaryButtonRequest={hide}
            showPrimaryButton={false}
            cancelButtonText="Close"
        >
            <div>
                <div className="row">
                    <div className="col-sm-12 col-md-6">
                        <h4>Current Groupings</h4>
                    </div>
                    <div className="col-sm-12 col-md-6">
                        <Autocomplete
                            id="table-search"
                            autoComplete
                            options={variableGroups}
                            onChange={(_, value) => setFilter(value?.GroupName ?? "")}
                            getOptionLabel={option => option.GroupName}
                            getOptionSelected={(option, value) => value.Id === option.Id}
                            renderOption={option => (
                                <React.Fragment>
                                    {option.GroupName} - {option.Description}
                                </React.Fragment>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Search"
                                    variant="standard"
                                    helperText={"Type here to search for name"}
                                    fullWidth
                                />
                            )}
                        />
                    </div>
                </div>
                {
                    <>
                        <Paper>
                            <Table size="medium">
                                <TableHeader
                                    headers={[
                                        ["Id", "Id", "smDown"],
                                        ["GroupName", "GroupName", "smDown"],
                                        ["Description", "Description", "mdDown"],
                                        ["Actions", "Actions", "smDown"],
                                    ]}
                                    orderBy={orderBy}
                                    order={order}
                                    onClick={handleRequestSort}
                                />
                                <TableBody
                                    data={data}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    emptyMessage={["EmptyMessage", "No Data to Display.", "smDown"]}
                                />
                            </Table>
                            <TableFooter
                                displayDense={false}
                                displayPaging={true}
                                rowsPerPageOptions={[5, 10, 15]}
                                count={tableDataFilteredByName.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                onRowsPerPageChange={e => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </Paper>{" "}
                    </>
                }
                <h4>Add New Grouping</h4>
                <Paper style={{ padding: "0px 10px 10px 10px" }}>
                    <div>
                        <div style={{ display: "flex", width: "100%" }}>
                            <ThemeProvider theme={theme}>
                                <FormGroupWithError errorText={errors.name}>
                                    <FormControl style={{ minWidth: "15vw", paddingRight: "10px" }}>
                                        <TextField
                                            inputProps={{ style: { fontSize: "15px" }, maxLength: 128 }}
                                            label={"Group Name"}
                                            disabled={requestPending}
                                            type="text"
                                            value={name}
                                            onChange={e => handleChangeName(e.target.value)}
                                        />
                                    </FormControl>
                                </FormGroupWithError>
                                <FormControl style={{ minWidth: "15vw", paddingRight: "10px" }}>
                                    <TextField
                                        inputProps={{ style: { fontSize: "15px" }, maxLength: 128 }}
                                        label={"Description"}
                                        disabled={requestPending}
                                        type="text"
                                        value={description}
                                        onChange={e => handleChangeDescription(e.target.value)}
                                    />
                                </FormControl>
                            </ThemeProvider>
                        </div>
                    </div>
                </Paper>
                <div style={{ marginLeft: "auto", paddingTop: "27px" }} className="text-right">
                    <ColorButton
                        style={{ fontSize: "14px", fontWeight: "normal" }}
                        onClick={() => save()}
                        disabled={requestPending}
                        backgroundcolor="#406eb3"
                    >
                        <span style={{ padding: "0px 1px" }}>Add Group</span>
                    </ColorButton>
                </div>
            </div>
        </MaterialModal>
    ) : null;
};

export default withModal(VariableGroupingModal, "VARIABLE_GROUPING");
